import { useTranslation } from 'react-i18next';

import { EvaluationPager, useEvaluationPager } from './EvaluationPager';
import {
    EVALUATION_LIST_ITEM,
    EVALUATION_LIST_ROW,
    EVALUATION_PANEL_INNER_LIST,
    EVALUATION_PANEL_INNER_SURFACE,
    EvaluationPanel,
} from './EvaluationPanel';
import { EvaluationStartButton } from './EvaluationStartButton';

import { useGetUserById } from '@/api/directory';
import { useGetManagerAssessments } from '@/api/performance-profile';
import { TileLink } from '@/components/TileLink';
import { useNavigate } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { evaluationDestination } from '@/navigation/evaluationDestination';
import { showWarningToast } from '@/plugins/Toast';

interface ManagerAssessmentPanelProps {
    employeeId?: string;
    isEmployeeInactive?: boolean;
    canStartEvaluations?: boolean;
    className?: string;
}

export const ManagerAssessmentPanel = ({
    employeeId,
    isEmployeeInactive,
    canStartEvaluations = true,
    className,
}: ManagerAssessmentPanelProps) => {
    const { t } = useTranslation();
    const { getManagerLabel } = useCompanyInformation();
    const navigate = useNavigate();

    // Get current user to check if trying to assess themselves
    const { data: currentUser } = useGetUserById({ userId: 'me' });

    const { data: assessments, isLoading, error } = useGetManagerAssessments(employeeId);
    const { pageIndex, setPageIndex, pageCount, visible } = useEvaluationPager(assessments, 4);

    const handleStartManagerAssessment = () => {
        // Check if trying to assess themselves
        if (currentUser && employeeId && currentUser.id === employeeId) {
            showWarningToast({
                title: t('performanceProfile.evaluations.manager.selfAssessmentWarningTitle'),
                message: t('performanceProfile.evaluations.manager.selfAssessmentWarningMessage', {
                    managerLabel: getManagerLabel(),
                }),
            });
            return;
        }

        navigate({
            path: '/performance-management/manager-assessment',
            state: { preselectedEmployeeId: employeeId },
        });
    };

    return (
        <EvaluationPanel
            title={t('performanceProfile.evaluations.manager.title', {
                managerLabel: getManagerLabel(),
            })}
            description={t('performanceProfile.evaluations.manager.description')}
            className={className}
        >
            {isLoading ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.loading')}
                    </div>
                </div>
            ) : error ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-error text-[14px]'}>
                        {t('performanceProfile.evaluations.manager.loadError', {
                            managerLabel: getManagerLabel(),
                        })}
                    </div>
                </div>
            ) : !assessments || assessments.length === 0 ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.manager.noAssessments', {
                            managerLabel: getManagerLabel(),
                        })}
                    </div>
                </div>
            ) : (
                <ul className={EVALUATION_PANEL_INNER_LIST}>
                    {visible.map((entry) => (
                        <li
                            key={entry.reviewInstanceId}
                            className={EVALUATION_LIST_ITEM}
                        >
                            <TileLink
                                to={evaluationDestination({
                                    reviewInstanceId: entry.reviewInstanceId,
                                    cycleInstanceId: entry.cycleInstanceId,
                                    managerAssessment: {
                                        step: entry.managerAssessmentStep,
                                        isSurveySubmitted: entry.isSurveySubmitted,
                                        isMeetingCompleted: entry.isMeetingCompleted,
                                    },
                                })}
                                ariaLabel={t('performanceProfile.evaluations.manager.openLabel', {
                                    date: entry.date,
                                    name: entry.managerName,
                                })}
                                className={EVALUATION_LIST_ROW}
                            >
                                <span>{entry.date}</span>
                                <span>{entry.managerName}</span>
                            </TileLink>
                        </li>
                    ))}
                </ul>
            )}

            <EvaluationPager
                pageIndex={pageIndex}
                pageCount={pageCount}
                onPickPage={setPageIndex}
            />

            {!isEmployeeInactive && (
                <EvaluationStartButton
                    canStartEvaluations={canStartEvaluations}
                    disabled={false}
                    // `develop` inlined this handler here; ours is the same code, already
                    // extracted above. Keeping the named one avoids two copies of the
                    // self-assessment guard drifting apart.
                    onClick={handleStartManagerAssessment}
                    tooltip={t('performanceProfile.evaluations.disabledTooltip')}
                    iconName={'arrow-export-3'}
                >
                    {t('performanceProfile.evaluations.manager.startButton')}
                </EvaluationStartButton>
            )}
        </EvaluationPanel>
    );
};
